import type { SheetFrame } from "../../types";

// Selectable scales (1:x) — from 1:100 up to 1:100000 for an orientation/overview sheet.
export const SCALE_OPTIONS = [100, 250, 500, 1000, 2000, 2500, 5000, 10000, 25000, 50000, 100000];

// Pure frame math in PUWG METERS — mirrors backend formatka/layout.py exactly
// (same corner order LL→LR→UR→UL pre-rotation, same CCW rotation), so what the
// editor shows is what the DXF viewport will frame.

/** Model-space footprint [m] of the template's paper map window at 1:scale. */
export function frameSizeMeters(
  viewportSizeMm: number[] | undefined,
  scaleDenominator: number,
): [number, number] {
  const [wMm, hMm] =
    viewportSizeMm && viewportSizeMm.length === 2 ? viewportSizeMm : [180, 230];
  return [(wMm * scaleDenominator) / 1000, (hMm * scaleDenominator) / 1000];
}

export function normalizeDeg(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** On-axis readout of a sheet: paper size (cm) → scale → ground coverage (m),
 *  per X/Y axis, accounting for the 0/90° map rotation (which swaps X↔Y). The
 *  paper dims come from the TEMPLATE's viewport; the meters = paper × scale. */
export function frameReadout(
  frame: { rotation_deg: number; scale_denominator: number },
  viewportSizeMm: number[] | undefined,
): { xCm: number; yCm: number; scale: number; xM: number; yM: number } {
  const [vw, vh] = viewportSizeMm && viewportSizeMm.length === 2 ? viewportSizeMm : [180, 230];
  const rotated = Math.round((frame.rotation_deg || 0) / 90) % 2 !== 0;
  const mmX = rotated ? vh : vw;
  const mmY = rotated ? vw : vh;
  const s = frame.scale_denominator;
  return { xCm: mmX / 10, yCm: mmY / 10, scale: s, xM: (mmX * s) / 1000, yM: (mmY * s) / 1000 };
}

const _num = (v: number, d = 1) => v.toLocaleString("pl-PL", { maximumFractionDigits: d });

/** "20 × 23 cm : 1:500 : 100 × 115 m" — the cm : skala : metry readout. */
export function formatFrameReadout(
  frame: { rotation_deg: number; scale_denominator: number },
  viewportSizeMm: number[] | undefined,
): string {
  const r = frameReadout(frame, viewportSizeMm);
  return `${_num(r.xCm)} × ${_num(r.yCm)} cm : 1:${r.scale} : ${_num(r.xM)} × ${_num(r.yM)} m`;
}

function rotate(x: number, y: number, deg: number): [number, number] {
  const t = (deg * Math.PI) / 180;
  return [x * Math.cos(t) - y * Math.sin(t), x * Math.sin(t) + y * Math.cos(t)];
}

/** The 4 corners (LL, LR, UR, UL pre-rotation) of a frame in PUWG meters. */
export function frameCornersPuwg(
  frame: Pick<SheetFrame, "center" | "rotation_deg">,
  sizeM: [number, number],
): [number, number][] {
  const [cx, cy] = frame.center;
  const [w, h] = sizeM;
  const offsets: [number, number][] = [
    [-w / 2, -h / 2],
    [w / 2, -h / 2],
    [w / 2, h / 2],
    [-w / 2, h / 2],
  ];
  return offsets.map(([dx, dy]) => {
    const [rx, ry] = rotate(dx, dy, frame.rotation_deg);
    return [cx + rx, cy + ry];
  });
}

/**
 * Rotation derived FROM corner geometry (LL→LR edge vector) — sign-proof: no
 * reliance on geoman's angle convention. After any map interaction we read the
 * polygon's corners back in PUWG and recover the canonical rotation here.
 */
export function rotationFromCornersPuwg(corners: [number, number][]): number {
  const [ll, lr] = corners;
  return normalizeDeg((Math.atan2(lr[1] - ll[1], lr[0] - ll[0]) * 180) / Math.PI);
}

/** Centroid of the 4 corners = the frame center. */
export function centroidPuwg(corners: [number, number][]): [number, number] {
  const sx = corners.reduce((a, c) => a + c[0], 0);
  const sy = corners.reduce((a, c) => a + c[1], 0);
  return [sx / corners.length, sy / corners.length];
}

// ── neighbour preview (mirror of backend adjacent_sheet_labels) ──────────────
// Rectangles are convex, so a Sutherland–Hodgman clip gives their overlap. The
// generator's rule, per sheet and per EDGE of its map window: just outside the
// edge lies a strip `tol` wide (a fraction of the shorter window side, so abutting
// sheets with a micro-gap still register); another sheet of the set describes
// that edge when it enters the strip with AREA (a shared line = exact duplicate
// does not count) over a run ≥ minSharedM (a corner touch does not count). So a
// tile abutting on the right, a sheet overlapping na zakładkę, and a window of a
// DIFFERENT SCALE (a 1:500 detail sheet inside the same parcel's 1:1000 sheet)
// all get badges, while a window lying fully inside another gets none on the
// outer sheet — the same rule the generator applies, so every badge shown here
// materialises as a „ŁĄCZY ARKUSZ N" label in the DXF.

function _segIntersect(
  p1: [number, number], p2: [number, number],
  a: [number, number], b: [number, number],
): [number, number] {
  const [x1, y1] = p1, [x2, y2] = p2, [x3, y3] = a, [x4, y4] = b;
  const den = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);
  if (Math.abs(den) < 1e-9) return p2;
  const t = ((x1 - x3) * (y3 - y4) - (y1 - y3) * (x3 - x4)) / den;
  return [x1 + t * (x2 - x1), y1 + t * (y2 - y1)];
}

/** Convex polygon intersection (subject clipped by a CCW clip ring). */
function _clipConvex(
  subject: [number, number][], clip: [number, number][],
): [number, number][] {
  let output = subject;
  for (let i = 0; i < clip.length; i++) {
    const a = clip[i];
    const b = clip[(i + 1) % clip.length];
    // Inside = left of the directed edge a→b (clip ring is CCW in PUWG x-east/y-north).
    const inside = (p: [number, number]) =>
      (b[0] - a[0]) * (p[1] - a[1]) - (b[1] - a[1]) * (p[0] - a[0]) >= -1e-9;
    const input = output;
    output = [];
    for (let j = 0; j < input.length; j++) {
      const cur = input[j];
      const prev = input[(j + input.length - 1) % input.length];
      if (inside(cur)) {
        if (!inside(prev)) output.push(_segIntersect(prev, cur, a, b));
        output.push(cur);
      } else if (inside(prev)) {
        output.push(_segIntersect(prev, cur, a, b));
      }
    }
    if (output.length === 0) return [];
  }
  return output;
}

/** Paper edge of a map window (mirror of layout.ADJACENT_EDGES): V+ right,
 *  V- left, H+ top, H- bottom. A frame rotated by phi in the model lands in its
 *  viewport un-rotated, so the frame's local +x is always the paper's right edge. */
export type SheetEdge = "V+" | "V-" | "H+" | "H-";

export interface AdjacentPair {
  a: number; // order of the sheet whose edge is described
  b: number; // order of the sheet lying beyond that edge
  edge: SheetEdge; // which edge of sheet `a`
  anchor: [number, number]; // PUWG point on that edge, middle of the covered run
}

/** Shoelace area of a convex ring (as produced by _clipConvex). */
function _ringArea(ring: [number, number][]): number {
  let acc = 0;
  for (let i = 0; i < ring.length; i++) {
    const [x1, y1] = ring[i];
    const [x2, y2] = ring[(i + 1) % ring.length];
    acc += x1 * y2 - x2 * y1;
  }
  return Math.abs(acc) / 2;
}

/** CCW ring of an axis-aligned box (in the labelled frame's local coordinates). */
function _box(x0: number, y0: number, x1: number, y1: number): [number, number][] {
  return [[x0, y0], [x1, y0], [x1, y1], [x0, y1]];
}

/** Edges of `frame` beyond which `other` lies (mirror of adjacent_sheet_labels
 *  for one neighbour) — the labels the generator stamps on `frame`'s sheet. */
function _edgesContinuedBy(
  frame: Pick<SheetFrame, "center" | "rotation_deg" | "scale_denominator">,
  other: Pick<SheetFrame, "center" | "rotation_deg" | "scale_denominator">,
  viewportSizeMm: number[] | undefined,
  minShared: number,
  tolFrac: number,
): { edge: SheetEdge; anchor: [number, number] }[] {
  const [w, h] = frameSizeMeters(viewportSizeMm, frame.scale_denominator);
  const tol = tolFrac * Math.min(w, h);
  const [cx, cy] = frame.center;
  const phi = frame.rotation_deg || 0;
  // Neighbour footprint in THIS frame's local (un-rotated, centred) coordinates.
  const local = frameCornersPuwg(other, frameSizeMeters(viewportSizeMm, other.scale_denominator))
    .map(([x, y]) => rotate(x - cx, y - cy, -phi));
  const strips: [SheetEdge, [number, number][]][] = [
    ["V+", _box(w / 2, -h / 2, w / 2 + tol, h / 2)],
    ["V-", _box(-w / 2 - tol, -h / 2, -w / 2, h / 2)],
    ["H+", _box(-w / 2, h / 2, w / 2, h / 2 + tol)],
    ["H-", _box(-w / 2, -h / 2 - tol, w / 2, -h / 2)],
  ];
  const out: { edge: SheetEdge; anchor: [number, number] }[] = [];
  for (const [edge, strip] of strips) {
    const inter = _clipConvex(local, strip);
    if (inter.length < 3 || _ringArea(inter) <= 1e-9) continue; // not beyond this edge
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of inter) {
      minX = Math.min(minX, x); maxX = Math.max(maxX, x);
      minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
    const vertical = edge.startsWith("V");
    if ((vertical ? maxY - minY : maxX - minX) < minShared) continue; // corner only
    const lx = vertical ? (edge === "V+" ? w / 2 : -w / 2) : (minX + maxX) / 2;
    const ly = vertical ? (minY + maxY) / 2 : (edge === "H+" ? h / 2 : -h / 2);
    const [ax, ay] = rotate(lx, ly, phi);
    out.push({ edge, anchor: [cx + ax, cy + ay] });
  }
  return out;
}

/** Sheet-edge contacts, with the on-edge anchor — the editor draws one „N ↔ M"
 *  badge per contact as a preview of what the generator stamps per sheet on layer
 *  FORMATKA_SASIEDNIE. Two abutting tiles share ONE contact (the right edge of
 *  one is the left edge of the other → same anchor, one badge); a detail sheet
 *  inside a larger one yields a badge on every edge the outer sheet continues past. */
export function adjacentSheetPairsPuwg(
  frames: Pick<SheetFrame, "order" | "center" | "rotation_deg" | "scale_denominator">[],
  viewportSizeMm: number[] | undefined,
  opts: { minSharedM?: number; tolFrac?: number } = {},
): AdjacentPair[] {
  const minShared = opts.minSharedM ?? 5;
  const tolFrac = opts.tolFrac ?? 0.02;
  const out: AdjacentPair[] = [];
  const seen = new Set<string>();
  for (let i = 0; i < frames.length; i++) {
    for (let j = 0; j < frames.length; j++) {
      if (i === j) continue;
      for (const hit of _edgesContinuedBy(frames[i], frames[j], viewportSizeMm, minShared, tolFrac)) {
        // Both directions of an abutting pair land on the same shared-edge point.
        const lo = Math.min(frames[i].order, frames[j].order);
        const hi = Math.max(frames[i].order, frames[j].order);
        const key = `${lo}-${hi}:${hit.anchor[0].toFixed(1)}:${hit.anchor[1].toFixed(1)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push({ a: frames[i].order, b: frames[j].order, edge: hit.edge, anchor: hit.anchor });
      }
    }
  }
  return out;
}

// ── edge snapping (axis-aligned; rotation is 0°/90° only) ────────────────────
// Snap radius as a fraction of the smaller frame side (95 m frame → ~4.75 m).
const SNAP_FRACTION = 0.05;

interface SnapFrame {
  center: [number, number];
  rotation_deg: number;
  scale_denominator: number;
}

/** Axis-aligned half-extents [hx, hy] in PUWG meters. Frames are constrained to
 *  poziom/pion (0°/90°), so a 90°-rotated frame just swaps width/height. */
export function halfExtents(f: SnapFrame, viewportSizeMm: number[] | undefined): [number, number] {
  const [w, h] = frameSizeMeters(viewportSizeMm, f.scale_denominator);
  const rotated = Math.round(f.rotation_deg / 90) % 2 !== 0;
  return rotated ? [h / 2, w / 2] : [w / 2, h / 2];
}

/**
 * Guide-line snapping, per axis, INDEPENDENTLY — the predictable behaviour of
 * design tools. For each neighbour we build guide lines at its left/center/right
 * (X) and bottom/center/top (Y) edges; the moved frame's own left/center/right
 * and bottom/center/top features snap to the nearest guide within threshold.
 *
 * Crucially: X-snapping only engages for neighbours that OVERLAP vertically
 * (and Y-snapping for horizontal overlap), so a frame never jumps sideways to
 * align with something it isn't actually next to — that killed the old
 * "snaps to god-knows-what" behaviour. Edge-to-edge adjacency (M.right → O.left)
 * and grid alignment (M.left → O.left) both fall out naturally. The closest
 * correction on each axis wins; no candidate → that axis is left untouched.
 */
export function snapFrameCenter(
  moved: SnapFrame,
  others: SnapFrame[],
  viewportSizeMm: number[] | undefined,
): [number, number] {
  const [mx, my] = moved.center;
  const [mhx, mhy] = halfExtents(moved, viewportSizeMm);
  const thresh = SNAP_FRACTION * Math.min(mhx, mhy) * 2;

  let bestDX = 0;
  let bestDY = 0;
  let adx = Infinity;
  let ady = Infinity;

  for (const o of others) {
    const [ox, oy] = o.center;
    const [ohx, ohy] = halfExtents(o, viewportSizeMm);
    const yOverlap = Math.abs(my - oy) <= mhy + ohy + thresh;
    const xOverlap = Math.abs(mx - ox) <= mhx + ohx + thresh;

    if (yOverlap) {
      const guides = [ox - ohx, ox, ox + ohx];
      const feats = [mx - mhx, mx, mx + mhx];
      for (const g of guides) {
        for (const fpos of feats) {
          const d = g - fpos; // move the moved center by d so `fpos` lands on `g`
          if (Math.abs(d) <= thresh && Math.abs(d) < adx) {
            adx = Math.abs(d);
            bestDX = d;
          }
        }
      }
    }
    if (xOverlap) {
      const guides = [oy - ohy, oy, oy + ohy];
      const feats = [my - mhy, my, my + mhy];
      for (const g of guides) {
        for (const fpos of feats) {
          const d = g - fpos;
          if (Math.abs(d) <= thresh && Math.abs(d) < ady) {
            ady = Math.abs(d);
            bestDY = d;
          }
        }
      }
    }
  }
  return [mx + bestDX, my + bestDY];
}

/** Smallest scale from SCALE_OPTIONS whose frame covers width×height meters
 *  (×1.05 margin), trying both orientations. Used by the orientation sheet. */
export function pickCoveringScale(
  widthM: number,
  heightM: number,
  viewportSizeMm: number[] | undefined,
): { scale: number; rotated: boolean } {
  const [vwMm, vhMm] = viewportSizeMm && viewportSizeMm.length === 2 ? viewportSizeMm : [180, 230];
  for (const s of SCALE_OPTIONS) {
    const fw = (vwMm * s) / 1000;
    const fh = (vhMm * s) / 1000;
    if (fw >= widthM * 1.05 && fh >= heightM * 1.05) return { scale: s, rotated: false };
    if (fh >= widthM * 1.05 && fw >= heightM * 1.05) return { scale: s, rotated: true };
  }
  return { scale: SCALE_OPTIONS[SCALE_OPTIONS.length - 1], rotated: widthM > heightM };
}
